import {JsonObject} from "../shared/json/json-object";
import {
  $KTS,
  BaseListItem,
  BaseListItemsLoader,
  DisplayName,
  Filter,
  KeyTextString,
  KeyTextStrings
} from "../shared/types";
import {JsonProperty} from "../shared/json/json-property";
import {md5_code, md5_uuid} from "../shared/md5";
import {FormGen} from "../shared/formgen";
import {JSON_OBJECT} from "../shared/json/helpers";

export enum Gender {
  MALE = "male",
  FEMALE = "female",
  OTHER = "other",
}

export const GENDER_KTS = new KeyTextStrings([
  $KTS(Gender.MALE, "Male"),
  $KTS(Gender.FEMALE, "Female"),
  $KTS(Gender.OTHER, "Other"),
]);

@JsonObject()
export class PatientBasicInfo {

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
    description: "This photo is publicly visible to anyone on your online profile",
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "First Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  firstname: string;

  @FormGen({
    name: "Last Name",
    type: "string",
    noneditable: true,
    required: true,
  })
  @JsonProperty()
  lastname: string;

  @FormGen({
    name: "Email",
    type: "string",
    inputMode: "email",
    noneditable: true,
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Gender",
    type: "enum",
    required: true,
    enumValues: GENDER_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  gender: string;

  @FormGen({
    name: "Phone Number",
    type: "string",
    inputMode: "numeric",
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Date of Birth",
    type: "datenum",
  })
  @JsonProperty()
  dateOfBirth: number;
}

@JsonObject()
export class PatientAddress {

  @FormGen({
    name: "House No., or Flat, with Street name",
    type: "string",
  })
  @JsonProperty()
  houseNumber: string;

  @FormGen({
    name: "Locality, Colony or Area",
    type: "string",
  })
  @JsonProperty()
  locality: string;

  @FormGen({
    name: "City",
    type: "string",
  })
  @JsonProperty()
  city: string;

  @FormGen({
    name: "State",
    type: "string",
  })
  @JsonProperty()
  state: string;

  @FormGen({
    name: "Country",
    type: "country_select",
  })
  @JsonProperty()
  country: string;

  @FormGen({
    name: "Pin or Zip Code",
    type: "string",
  })
  @JsonProperty()
  zipCode: string;
}

@JsonObject()
export class Patient extends BaseListItem {

  @JsonProperty()
  readonly shortcode: string;

  @FormGen({
    name: "Basic Info",
    type: "object",
  })
  @JsonProperty()
  readonly basicInfo: PatientBasicInfo = new PatientBasicInfo();

  @FormGen({
    name: "Address",
    type: "object",
    separator: "above",
  })
  @JsonProperty()
  readonly address: PatientAddress = new PatientAddress();

  static createNew(): Patient {
    return new Patient(md5_uuid());
  }

  constructor(id: string) {
    super(id);
    this.shortcode = Math.abs(md5_code(id)).toString().substring(0, 6);
  }

  displayName(): string {
    return DisplayName(this.basicInfo.firstname, this.basicInfo.lastname);
  }

  displayNameWithEmail(): string {
    return `${DisplayName(this.basicInfo.firstname, this.basicInfo.lastname)} <${this.basicInfo.email}>`;
  }
}

export class Patients extends BaseListItemsLoader<Patient> {

  private static instance: Patients;

  static getInstance(): Patients {
    if (!this.instance) {
      this.instance = new Patients();
    }
    return this.instance;
  }

  private constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "patients";
  }

  protected deserializeItem(value: any): Patient {
    return JSON_OBJECT.deserializeObject(value, Patient);
  }

  protected serializeItem(item: Patient): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Patient, item2: Patient): number {
    return item1.basicInfo.firstname?.localeCompare(item2.basicInfo.firstname)
      || item1.basicInfo.lastname?.localeCompare(item2.basicInfo.lastname);
  }
}

export function patientAutocompleteLoader() {
  const patients = Patients.getInstance();
  return new Promise<KeyTextString[]>((resolve, reject) => {
    patients.getOrLoadListItems()
      .then(patients => resolve(patients.map(patient => $KTS(patient.id, patient.displayName()))))
      .catch(reason => reject(reason));
  });
}

export function PatientFilter<T extends { patientId: string }>(patientId: string): Filter<T> {
  return item => !patientId || (item.patientId === patientId);
}

@JsonObject()
export class CaseFile {

  @JsonProperty()
  readonly url: string;

  @JsonProperty()
  readonly created: number;

  @JsonProperty()
  readonly mimeType: string;

  @JsonProperty()
  readonly fileSize: number;

  @JsonProperty()
  name: string;

  constructor(url: string, created: number, mimeType: string, fileSize: number, name?: string) {
    this.url = url;
    this.created = created;
    this.mimeType = mimeType;
    this.fileSize = fileSize;
    this.name = name;
  }
}

export enum CaseStatus {
  ARCHIVED = "archived",
  ACTIVE = "active",
}

export const CASES_STATUS_KTS = new KeyTextStrings([
  $KTS(CaseStatus.ARCHIVED, "Archived"),
  $KTS(CaseStatus.ACTIVE, "Active"),
]);

@JsonObject()
export class Case extends BaseListItem {

  @FormGen({name: "Title", type: "string"})
  @JsonProperty()
  title: string = "New Case";

  @JsonProperty()
  status: CaseStatus = CaseStatus.ACTIVE;

  patient: Patient;

  @FormGen({type: "autocomplete", name: "Patient", autocompleteLoader: patientAutocompleteLoader})
  @JsonProperty()
  patientId: string;

  @JsonProperty()
  readonly files: CaseFile[] = [];

  static createNew(patientId: string): Case {
    const case_ = new Case(md5_uuid());
    case_.patientId = patientId;
    return case_;
  }

  constructor(id: string) {
    super(id);
  }
}

export class Cases extends BaseListItemsLoader<Case> {

  private static instance;

  static getInstance(): Cases {
    if (!this.instance) {
      this.instance = new Cases();
    }
    return this.instance;
  }

  private readonly patients = Patients.getInstance();

  constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "cases";
  }

  protected async onAfterItemDeserialized(item: Case, val?: any): Promise<Case> {
    item.patient = await this.patients.getOrLoadItem(item.patientId);
    return super.onAfterItemDeserialized(item, val);
  }


  protected deserializeItem(value: any): Case {
    return JSON_OBJECT.deserializeObject(value, Case);
  }

  protected serializeItem(item: Case): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Case, item2: Case): number {
    return item2.created - item1.created;
  }
}